/**
 * Read-only, syntax-highlighted source.
 *
 * Reuses the highlight engine (`rehype-highlight`) and theme
 * (`styles/syntax-highlight.css`) that `MarkdownContent` already uses, so code
 * looks identical wherever it appears. Highlighting works by wrapping the
 * source in a fenced block and running it through the same markdown pipeline —
 * a trick previously inlined privately in `CreateAutomationRenderer`, lifted
 * here so the second consumer (the builder panel's Code tab) doesn't reproduce
 * it and let the two drift.
 *
 * This is a VIEWER, not an editor. Nothing in the codebase edits code in the
 * browser today (no monaco, no codemirror), and automation scripts are authored
 * by the agent rather than typed by hand.
 */

import '@/styles/syntax-highlight.css';

import { useMemo } from 'react';
import ReactMarkdown, { type Components } from 'react-markdown';
import rehypeHighlight from 'rehype-highlight';

import { cx } from '@/utils/cx';

const REHYPE_PLUGINS = [rehypeHighlight];

/**
 * Bare, background-less `<pre>` so the block inherits its container's surface.
 * Callers that want a card should supply their own wrapper.
 */
const TRANSPARENT_COMPONENTS: Components = {
  pre: ({ children }) => (
    <pre className='m-0 bg-transparent p-0 font-mono text-xs leading-relaxed'>
      {children}
    </pre>
  ),
  code: ({ className, children }) => (
    <code className={cx('font-mono', className)}>{children}</code>
  ),
};

/** Panelled variant — its own surface, for when the code IS the content. */
const SURFACE_COMPONENTS: Components = {
  pre: ({ children }) => (
    <pre className='m-0 overflow-x-auto rounded-lg border border-[var(--color-border-secondary)] bg-[var(--color-bg-secondary)] p-3 font-mono text-xs leading-relaxed'>
      {children}
    </pre>
  ),
  code: ({ className, children }) => (
    <code className={cx('font-mono', className)}>{children}</code>
  ),
};

export interface CodeBlockProps {
  /** The source to render. Rendered verbatim — never interpreted. */
  source: string;
  /** Highlight language. Defaults to python (every automation script today). */
  language?: string;
  /**
   * When true the block has no surface of its own and blends into its parent.
   * Use inside an existing card; leave off when the code stands alone.
   */
  transparent?: boolean;
  className?: string;
}

export function CodeBlock({
  source,
  language = 'python',
  transparent = false,
  className,
}: CodeBlockProps) {
  // Fence the source so the markdown pipeline highlights it. Memoised because
  // scripts run to hundreds of lines and this rebuilds the whole string.
  const markdown = useMemo(
    () => '```' + language + '\n' + source + '\n```',
    [language, source]
  );

  return (
    <div className={cx('overflow-x-auto', className)}>
      <ReactMarkdown
        components={transparent ? TRANSPARENT_COMPONENTS : SURFACE_COMPONENTS}
        rehypePlugins={REHYPE_PLUGINS}
      >
        {markdown}
      </ReactMarkdown>
    </div>
  );
}
